import { Mail } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input, Label, NativeSelect } from '@/components/ui/input';
import { useConnectEmail, type EmailConnectBody } from './api';

/** Kết nối hộp thư email riêng của NV (CR-014 bước 7a, DEC-232). Mật khẩu = mật khẩu ứng dụng, lưu mã hóa trên máy chủ. */
const HELP: Record<EmailConnectBody['provider'], string> = {
  gmail: 'Gmail: bật Xác minh 2 bước → myaccount.google.com/apppasswords → tạo mật khẩu ứng dụng 16 ký tự.',
  outlook: 'Outlook / Microsoft 365: bật Xác minh 2 bước → Bảo mật → Mật khẩu ứng dụng. Một số công ty tắt IMAP, hỏi quản trị email.',
  zoho: 'Zoho Mail: Cài đặt → Truy cập POP/IMAP bật IMAP → Bảo mật → Mật khẩu ứng dụng.',
  custom: 'Hộp thư tên miền riêng (VD @thepminhtien.com): hỏi nhà cung cấp email máy chủ IMAP (cổng 993) và SMTP (465 hoặc 587).',
};

export function EmailConnectForm({ initialEmail, onDone }: { initialEmail?: string; onDone: () => void }) {
  const connect = useConnectEmail();
  const [f, setF] = useState<EmailConnectBody>({ email: initialEmail ?? '', password: '', provider: 'gmail' });
  const set = (p: Partial<EmailConnectBody>) => setF({ ...f, ...p });
  const submit = () =>
    connect.mutateAsync({ ...f, email: f.email.trim(), password: f.password.replace(/\s+/g, '') }).then(
      () => {
        toast.success(`Đã kết nối ${f.email.trim()}. Thư mới về hộp thư sau vài phút.`);
        onDone();
      },
      (e: unknown) => toast.error(e instanceof Error ? e.message : 'Không kết nối được'),
    );
  return (
    <form
      className="space-y-2.5 rounded-lg border p-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="flex items-center gap-1.5 text-sm font-medium">
        <Mail className="size-4" /> Kết nối email của tôi
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Loại hộp thư</Label>
          <NativeSelect value={f.provider} onChange={(e) => set({ provider: e.target.value as EmailConnectBody['provider'] })}>
            <option value="gmail">Gmail / Google Workspace</option>
            <option value="outlook">Outlook / Microsoft 365</option>
            <option value="zoho">Zoho Mail</option>
            <option value="custom">Khác (tự nhập máy chủ)</option>
          </NativeSelect>
        </div>
        <div>
          <Label className="text-xs">Địa chỉ email</Label>
          <Input
            type="email"
            value={f.email}
            onChange={(e) => set({ email: e.target.value })}
            placeholder="ten@gmail.com"
            required
            maxLength={254}
            autoComplete="off"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Mật khẩu ứng dụng</Label>
          <Input type="password" value={f.password} onChange={(e) => set({ password: e.target.value })} required maxLength={200} autoComplete="new-password" />
        </div>
        {f.provider === 'custom' && (
          <>
            <div>
              <Label className="text-xs">Máy chủ IMAP</Label>
              <Input value={f.imapHost ?? ''} onChange={(e) => set({ imapHost: e.target.value })} placeholder="mail.tenmien.com" />
            </div>
            <div>
              <Label className="text-xs">Máy chủ SMTP</Label>
              <Input value={f.smtpHost ?? ''} onChange={(e) => set({ smtpHost: e.target.value })} placeholder="mail.tenmien.com" />
            </div>
            <div>
              <Label className="text-xs">Cổng IMAP</Label>
              <Input type="number" value={f.imapPort ?? 993} onChange={(e) => set({ imapPort: Number(e.target.value) || 993 })} />
            </div>
            <div>
              <Label className="text-xs">Cổng SMTP</Label>
              <Input type="number" value={f.smtpPort ?? 465} onChange={(e) => set({ smtpPort: Number(e.target.value) || 465 })} />
            </div>
          </>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{HELP[f.provider]} Chỉ bạn, trưởng phòng của bạn và giám đốc xem được thư trong ERP.</p>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Hủy
        </Button>
        <Button type="submit" size="sm" disabled={connect.isPending || !f.email.trim() || !f.password}>
          {connect.isPending ? 'Đang đăng nhập thử…' : 'Kết nối'}
        </Button>
      </div>
    </form>
  );
}
